// Core
import React, { useMemo } from 'react';

// Libraries
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import { formatValueWithUnit, roundNumber } from '../../../../../../plugins/general';
import {
  isEnabled,
  vibrationSensorToTagType,
  vibrationSensorToText,
} from '../../../../../../plugins/crusher';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Row } from '../../../../../../layout/elements/Row';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';

// Types
import { Theme } from '../../../../../../types/theme';

// View
import {
  VibrationContainer,
  VibrationItem,
  VibrationItemsContainer,
  VibrationTagContainer,
} from './Vibration.style';
import { VibrationProps, VibrationSensorList } from './Vibration.types';

function Vibration({
  className,
  edgeStatus,
  onOpenDrawer,
  testId,
  unitData,
}: VibrationProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  // Create reactive sensors array based on enabled sensors
  const enabledSensors: VibrationSensorList[] = useMemo(() => {
    const sensors: VibrationSensorList[] = [
      {
        name: 'vibrationSensor1',
        value: roundNumber(unitData?.vibrationSensor1),
        index: 0,
        enabled: unitData?.vibrationSensor1Enable,
      },
      {
        name: 'vibrationSensor2',
        value: roundNumber(unitData?.vibrationSensor2),
        index: 1,
        enabled: unitData?.vibrationSensor2Enable,
      },
      {
        name: 'vibrationSensor3',
        value: roundNumber(unitData?.vibrationSensor3),
        index: 2,
        enabled: unitData?.vibrationSensor3Enable,
      },
      {
        name: 'vibrationSensor4',
        value: roundNumber(unitData?.vibrationSensor4),
        index: 3,
        enabled: unitData?.vibrationSensor4Enable,
      },
    ];

    // Return only enabled sensors (even if value is null)
    // Consider sensors enabled if enabled is true OR null (default enabled)
    return sensors.filter((sensor: VibrationSensorList) => isEnabled(sensor.enabled));
  }, [unitData]);

  // Check if only one sensor is enabled
  const hasOnlyOneSensor: boolean = enabledSensors.length === 1;

  const renderSensorItem = (
    sensorName: string,
    sensorValue: number | null,
    index: number,
  ): JSX.Element => {
    const formattedValue: string = formatValueWithUnit(sensorValue, '%', 0);

    return (
      <VibrationItem key={sensorName} hasOnlyOneSensor={hasOnlyOneSensor}>
        <Row>
          <Col cols={11}>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.vibrationModule.nameTextColor}
            >
              {`${t('view.crusher.vibration.sensor')} ${index + 1}`}
            </Paragraph>
          </Col>
          <Col cols={9}>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.vibrationModule.valueTextColor}
            >
              {formattedValue}
            </Paragraph>
          </Col>
          <Col cols={4}>
            <VibrationTagContainer>
              <Tag
                text={vibrationSensorToText(sensorValue, { highWarning: 55 })}
                type={vibrationSensorToTagType(
                  sensorValue,
                  { highWarning: 55 },
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
              />
            </VibrationTagContainer>
          </Col>
        </Row>
      </VibrationItem>
    );
  };

  return (
    <VibrationContainer
      data-testid={testId}
      className={className}
    >
      <Row>
        <Col>
          <ModuleHeader
            onClick={onOpenDrawer}
            title={t('view.crusher.vibration.title')}
          />
        </Col>
      </Row>
      <VibrationItemsContainer>
        {enabledSensors.map((sensor: VibrationSensorList) => renderSensorItem(
          sensor.name,
          sensor.value,
          sensor.index,
        ))}
      </VibrationItemsContainer>
    </VibrationContainer>
  );
}

export { Vibration };
